import { Head } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assignTargets,
  createFlock,
  DEFAULT_FLOCK,
  DEFAULT_WIND,
  makeRng,
  releaseTargets,
  sampleText,
  sampleTwoTone,
  step,
  type Boid,
  type Vec,
} from './murmuration';

/**
 * TASK-068 — voler.emovia.com Easter Egg.
 *
 * A full-page murmuration of starlings over a golden-hour sky (canvas 2D).
 * The flock flies free, parts around the cursor/touch like wind, and every
 * ~22s (or on click/tap/Enter) converges to spell VOLEREMO VIA, then slides
 * into VOLER.EMOVIA with the EMOVIA tail tinted brand blue, holds, disperses.
 * After the first pun moment a typewritten question + a single CTA appear.
 *
 * SSR-safe: the first render is deterministic (sky + sr-only h1 + CTA already
 * in the DOM, no window/canvas/clock). Simulation and rendering live entirely
 * outside React — rAF + refs, zero per-frame setState. The boids physics is a
 * pure module (./murmuration) so it stays unit-testable without a DOM.
 *
 * Reduced motion: no loop. We draw a single static VOLER.EMOVIA formation and
 * reveal the question + CTA immediately.
 */

const QUESTION = 'Tu, verso cosa voleresti?';
const CTA_HREF = 'https://www.emovia.com';
const BRAND: [number, number, number] = [110, 168, 255]; // #6ea8ff, matches Andr
const PUN_INTERVAL_MS = 22000;
const FIRST_PUN_MS = 6000;

// Pun moment timeline (ms, relative to trigger).
const FORM1_HOLD = 2500; // hold on VOLEREMO VIA
const MORPH_HOLD = 3200; // hold on VOLER.EMOVIA
const FORM_IN = 1100; // converge time before we count a "hold"

type Phase = 'free' | 'form1' | 'morph' | 'release';

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

// Count scales with viewport area & DPR, then degrades at runtime under budget.
function targetCount(width: number, height: number, dpr: number): number {
  const area = width * height;
  const base = Math.round(area / 520); // denser flock: ~2500 on 1440×900
  const dprAdj = dpr > 1.5 ? base * 0.7 : base;
  return Math.max(900, Math.min(2800, Math.round(dprAdj)));
}

// Word layout: shared by the live loop and the reduced-motion static render so
// the two formations stay pixel-identical.
function computeLayout(width: number, height: number, count: number) {
  const stacked = width < 560;
  // Stacked size is driven by the longest line ("VOLEREMO", 8 chars) so it
  // never overflows narrow screens; wide screens scale on width/height.
  const fontPx = stacked
    ? Math.min(width * 0.155, height * 0.16)
    : Math.min(width * 0.13, height * 0.34);
  // Sampling density tracks the flock size so there are always enough boids to
  // cover every letter point (no gappy words), but stays fine enough to read.
  const sampleStep = count > 1800 ? 5 : 6;
  return { stacked, fontPx, sampleStep };
}

// Boid colour: dark slate, fading to brand blue as `tint` rises (EMOVIA tail).
function boidColor(tint: number, alpha: number): string {
  if (tint > 0.02) {
    const r = Math.round(28 + (BRAND[0] - 28) * tint);
    const g = Math.round(28 + (BRAND[1] - 28) * tint);
    const b = Math.round(34 + (BRAND[2] - 34) * tint);
    return `rgba(${r},${g},${b},${Math.min(1, alpha + tint * 0.4)})`;
  }
  return `rgba(24,22,30,${alpha})`;
}

const Voler = () => {
  const reducedMotion = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [question, setQuestion] = useState('');
  const [showCta, setShowCta] = useState(false);

  // All animation state in refs so the rAF loop never triggers a React render.
  const phaseRef = useRef<Phase>('free');
  const phaseStartRef = useRef(0);
  const lastPunRef = useRef(0);
  const triggerRef = useRef<() => void>(() => {});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reducedMotion) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // Failure mode: stay on the static SSR fallback.

    // Typewriter reveal, owned by this effect so its interval is cleaned up on
    // unmount and the rAF loop never depends on React state (no remount churn).
    let revealed = false;
    let typer: ReturnType<typeof setInterval> | null = null;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      setRevealed(true);
      let typed = 0;
      typer = setInterval(() => {
        typed += 1;
        setQuestion(QUESTION.slice(0, typed));
        if (typed >= QUESTION.length && typer) {
          clearInterval(typer);
          typer = null;
          setShowCta(true);
        }
      }, 55);
    };

    let width = 0;
    let height = 0;
    let dpr = 1;
    let boids: Boid[] = [];
    const rng = makeRng(Date.now() & 0xffffffff || 1);

    // Offscreen canvas for text sampling; cache by string + size signature.
    const off = document.createElement('canvas');
    const offCtx = off.getContext('2d');
    let targets1: Float32Array | null = null; // VOLEREMO VIA
    let targets2: Float32Array | null = null; // VOLER.EMOVIA
    let tailMask2: Uint8Array | null = null;

    let raf = 0;
    let running = true;
    let count = 0;

    // Frame-time degradation: if we blow the budget for a run of frames, trim.
    let slowFrames = 0;
    let lastTs = 0;
    const wind: Vec = { x: -1e9, y: -1e9 };
    let windScale = 0; // 0..1, damped during formations
    let pointerInside = false;

    const buildTargets = () => {
      if (!offCtx) return;
      off.width = width;
      off.height = height;
      const { stacked, fontPx, sampleStep } = computeLayout(width, height, count);

      targets1 = sampleText(
        offCtx,
        stacked ? ['VOLEREMO', 'VIA'] : ['VOLEREMO VIA'],
        { width, height },
        fontPx,
        sampleStep,
      );
      const two = sampleTwoTone(
        offCtx,
        ['VOLER.'],
        ['EMOVIA'],
        { width, height },
        fontPx,
        sampleStep,
      );
      targets2 = two.targets;
      tailMask2 = two.tailMask;
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const want = targetCount(width, height, dpr);
      if (boids.length === 0) {
        count = want;
        boids = createFlock(count, { width, height }, rng);
      } else {
        count = Math.min(count, want);
        boids = boids.slice(0, count);
      }
      buildTargets();
    };

    const setPhase = (p: Phase) => {
      phaseRef.current = p;
      phaseStartRef.current = performance.now();
      if (p === 'form1' && targets1) assignTargets(boids, targets1, rng);
      else if (p === 'morph' && targets2) assignTargets(boids, targets2, rng);
      else if (p === 'release') releaseTargets(boids);
    };

    const trigger = () => {
      if (phaseRef.current !== 'free') return;
      lastPunRef.current = performance.now();
      setPhase('form1');
    };
    triggerRef.current = trigger;

    let firstDone = false;
    const advance = (now: number) => {
      const phase = phaseRef.current;
      const since = now - phaseStartRef.current;
      if (phase === 'form1' && since > FORM_IN + FORM1_HOLD) setPhase('morph');
      else if (phase === 'morph' && since > FORM_IN + MORPH_HOLD) {
        setPhase('release');
        reveal();
        firstDone = true;
      } else if (phase === 'release' && since > 1600) phaseRef.current = 'free';
      else if (phase === 'free') {
        // First pun fires early (~6s) so the page never reads as an empty
        // screensaver; later ones settle into the long interval.
        const wait = firstDone ? PUN_INTERVAL_MS : FIRST_PUN_MS;
        if (now - lastPunRef.current > wait) {
          lastPunRef.current = now;
          setPhase('form1');
        }
      }
    };

    const frame = (ts: number) => {
      if (!running) return;
      advance(ts);

      const phase = phaseRef.current;
      const forming = phase === 'form1' || phase === 'morph';
      // Wind is damped while a word is settling so it doesn't shred.
      const targetWind = forming ? 0.25 : pointerInside ? 1 : 0;
      windScale += (targetWind - windScale) * 0.08;

      const activeTargets = phase === 'form1' ? targets1 : phase === 'morph' ? targets2 : null;
      const activeMask = phase === 'morph' ? tailMask2 : null;

      step(
        boids,
        { width, height },
        DEFAULT_FLOCK,
        DEFAULT_WIND,
        windScale > 0.01 ? wind : null,
        windScale,
        activeTargets,
        activeMask,
      );

      // --- draw ---
      ctx.clearRect(0, 0, width, height);
      const forming2 = phase === 'form1' || phase === 'morph';
      for (let i = 0; i < boids.length; i++) {
        const b = boids[i];
        // Depth drives scale + opacity for a sense of distance in the flock.
        const scale = 0.7 + b.depth * 1.3;
        const horizon = 0.5 + (b.y / height) * 0.4;
        const alpha = Math.min(1, (0.45 + b.depth * 0.5) * horizon);
        ctx.fillStyle = boidColor(b.tint, alpha);

        if (forming2) {
          // While composing the word, draw compact dots so it stays legible.
          ctx.beginPath();
          ctx.arc(b.x, b.y, 1.3 * scale, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }

        // Free flight: a tiny starling silhouette — a soft V (chevron) of two
        // swept wings, rotated to the heading. Reads as a bird, not a dot.
        const sp = Math.hypot(b.vx, b.vy) || 1;
        const hx = b.vx / sp;
        const hy = b.vy / sp;
        const px = -hy; // perpendicular (wing axis)
        const py = hx;
        // Wing flap: phase off the wander angle so the flock shimmers.
        const flap = 0.55 + 0.45 * Math.sin(b.wanderAngle * 3);
        const wing = (1.7 + b.depth * 1.7) * scale;
        const span = wing * flap;
        const back = wing * 0.5;
        // Wingtip points and a slight tail, forming a shallow chevron.
        const tipLx = b.x + px * span - hx * back;
        const tipLy = b.y + py * span - hy * back;
        const tipRx = b.x - px * span - hx * back;
        const tipRy = b.y - py * span - hy * back;
        const noseX = b.x + hx * wing * 0.45;
        const noseY = b.y + hy * wing * 0.45;
        ctx.beginPath();
        ctx.moveTo(tipLx, tipLy);
        ctx.lineTo(noseX, noseY);
        ctx.lineTo(tipRx, tipRy);
        ctx.lineTo(b.x - hx * back * 0.6, b.y - hy * back * 0.6);
        ctx.closePath();
        ctx.fill();
      }

      // Adaptive degradation: sustained slow frames trim the flock. Skip while
      // a word is forming so target assignments never go stale mid-formation.
      if (lastTs && phase === 'free') {
        const dt = ts - lastTs;
        if (dt > 22) slowFrames++;
        else slowFrames = Math.max(0, slowFrames - 1);
        if (slowFrames > 45 && boids.length > 500) {
          boids.length = Math.round(boids.length * 0.85);
          count = boids.length;
          slowFrames = 0;
          buildTargets();
        }
      }
      lastTs = ts;
      raf = requestAnimationFrame(frame);
    };

    resize();
    lastPunRef.current = performance.now();
    raf = requestAnimationFrame(frame);

    const onMove = (x: number, y: number) => {
      wind.x = x;
      wind.y = y;
      pointerInside = true;
    };
    const onPointerMove = (e: PointerEvent) => onMove(e.clientX, e.clientY);
    // Fires when the pointer actually leaves the window/document.
    const onPointerOut = (e: PointerEvent) => {
      if (!e.relatedTarget) pointerInside = false;
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) onMove(t.clientX, t.clientY);
    };
    const onTouchEnd = () => {
      pointerInside = false;
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        lastTs = 0;
        lastPunRef.current = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };

    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerout', onPointerOut, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      if (typer) clearInterval(typer);
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerout', onPointerOut);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reducedMotion]);

  // Static reduced-motion formation: draw VOLER.EMOVIA once, no loop. The text
  // + CTA are revealed regardless of canvas availability (never a dead end).
  useEffect(() => {
    if (!reducedMotion) return;
    setRevealed(true);
    setQuestion(QUESTION);
    setShowCta(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const off = document.createElement('canvas');
    off.width = width;
    off.height = height;
    const offCtx = off.getContext('2d');
    if (!offCtx) return;
    // count is irrelevant for the static frame; pass a typical value so the
    // sample density matches the live loop's default.
    const { fontPx, sampleStep } = computeLayout(width, height, 1200);
    const { targets, tailMask } = sampleTwoTone(
      offCtx,
      ['VOLER.'],
      ['EMOVIA'],
      { width, height },
      fontPx,
      sampleStep,
    );
    ctx.clearRect(0, 0, width, height);
    for (let i = 0; i < targets.length / 2; i++) {
      const x = targets[i * 2];
      const y = targets[i * 2 + 1];
      ctx.fillStyle = boidColor(tailMask[i] ? 1 : 0, 0.9);
      ctx.beginPath();
      ctx.arc(x, y, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [reducedMotion]);

  const onKeyTrigger = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerRef.current();
    }
  }, []);

  return (
    <div className="voler-root">
      <Head title="voleremovia" />
      <div className="voler-sky" aria-hidden="true" />
      <div className="voler-grain" aria-hidden="true" />
      <div className="voler-vignette" aria-hidden="true" />

      <canvas ref={canvasRef} className="voler-canvas" aria-hidden="true" />

      {/* Keyboard/click trigger for the pun moment (pointer trigger handled here too). */}
      <button
        type="button"
        className="voler-trigger"
        aria-label="Fai convergere lo stormo"
        onClick={() => triggerRef.current()}
        onKeyDown={onKeyTrigger}
      />

      <main className="voler-stage">
        {/* Always in the DOM so the page is never a dead end without JS. */}
        <h1 className="sr-only">voleremovia — Scopri Emovia</h1>

        <div className={`voler-text${revealed ? ' voler-text-on' : ''}`}>
          <p className="voler-question" aria-hidden={!showCta}>
            {question}
            {revealed && !showCta && <span className="voler-caret">|</span>}
          </p>
          <a className={`voler-cta${showCta ? ' voler-cta-on' : ''}`} href={CTA_HREF}>
            Scopri Emovia
            <span aria-hidden="true" className="voler-cta-arrow">
              →
            </span>
          </a>
        </div>
      </main>

      <style>{STYLES}</style>
    </div>
  );
};

const STYLES = `
.voler-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: #1a1620;
}
.voler-sky {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    #2a2546 0%,
    #4a3a63 28%,
    #8d5a73 52%,
    #d98c6a 74%,
    #f3b072 88%,
    #f7c98a 100%
  );
  animation: voler-sky 64s ease-in-out infinite alternate;
}
@keyframes voler-sky {
  0%   { filter: hue-rotate(0deg) saturate(1); }
  100% { filter: hue-rotate(-12deg) saturate(1.08); }
}
.voler-grain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.voler-vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(130% 110% at 50% 38%, transparent 52%, rgba(30, 18, 36, 0.55) 100%);
}
.voler-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
/* Invisible full-screen trigger sits under the text layer but over the canvas. */
.voler-trigger {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.voler-trigger:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: -4px;
}
.voler-stage {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: clamp(1.5rem, 6vh, 4rem) clamp(1rem, 5vw, 3rem);
  pointer-events: none;
}
.voler-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.9rem, 3vh, 1.6rem);
  text-align: center;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.voler-text-on { opacity: 1; transform: translateY(0); }
.voler-question {
  min-height: 1.5em;
  font-family: ui-sans-serif, system-ui, sans-serif;
  font-size: clamp(1rem, 4.2vw, 1.6rem);
  font-weight: 500;
  letter-spacing: 0.01em;
  color: rgba(40, 24, 30, 0.92);
  text-shadow: 0 1px 10px rgba(255, 220, 180, 0.4);
}
.voler-caret {
  margin-left: 1px;
  animation: voler-blink 0.9s steps(2, start) infinite;
}
@keyframes voler-blink { 50% { opacity: 0; } }
.voler-cta {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.9rem;
  border-radius: 999px;
  font-family: ui-sans-serif, system-ui, sans-serif;
  font-size: clamp(0.9rem, 3.6vw, 1.15rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  background: hsl(225 100% 42%);
  box-shadow: 0 10px 34px hsl(225 80% 30% / 0.45);
  text-decoration: none;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s ease;
}
.voler-cta-on { opacity: 1; transform: translateY(0); }
.voler-cta:hover { box-shadow: 0 12px 42px hsl(225 80% 30% / 0.6); }
.voler-cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 22, 32, 0.9), 0 0 0 6px #fff;
  opacity: 1;
  transform: translateY(0);
}
.voler-cta-arrow { transition: transform 0.2s ease; }
.voler-cta:hover .voler-cta-arrow { transform: translateX(4px); }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .voler-sky { animation: none; }
  .voler-caret { animation: none; display: none; }
  .voler-text { opacity: 1; transform: none; transition: none; }
  .voler-cta { opacity: 1; transform: none; }
}
`;

export default Voler;
